/**
 * MECANISMO da identidade do cliente (BLUEPRINT §2).
 *
 * Aqui vive o que é do MOLDE: os TIPOS da configuração, o vocabulário CRM×CRO
 * e os helpers (`matchDoctor`, `fullAddress`, `isOwnerEmail`, `isPixConfigured`).
 * Os VALORES — nome da clínica, profissional, médicos, convênios, Pix, fiscal,
 * hospital, sócios — moram em `src/cliente/clinica.ts` e são re-exportados
 * abaixo.
 *
 * Por que separado: atualizar um app de cliente com o molde sobrepõe `src/lib`
 * (mecanismo) e PRESERVA `src/cliente` (escolhas). Se a identidade vivesse aqui,
 * um sync apagaria o nome, o CRM e os profissionais do cliente.
 *
 * O app inteiro importa DAQUI (`@/lib/clinic-config`) — o re-export mantém a
 * API pública inalterada. Não importe de `@/cliente/clinica` direto.
 */

import {
  ASSISTENTE_CONFIG,
  CAPACIDADE_AGENDA,
  CLINIC_DOCTORS,
  CLINIC_INFO,
  CLINIC_TYPE,
  CLINICA_ID,
  CONVENIOS,
  DIAS_ATENDIMENTO,
  DOCTOR_INFO,
  ESPECIALIDADE,
  FISCAL_CONFIG,
  HORARIO_AGENDA,
  HOSPITAL_PADRAO,
  HOSPITAL_TELEFONE,
  OWNER_EMAILS,
  PIX_CONFIG,
  SHAREHOLDERS,
} from "@/shared/cliente/clinica";

export {
  ASSISTENTE_CONFIG,
  CAPACIDADE_AGENDA,
  CLINIC_DOCTORS,
  CLINIC_INFO,
  CLINIC_TYPE,
  CLINICA_ID,
  CONVENIOS,
  DIAS_ATENDIMENTO,
  DOCTOR_INFO,
  ESPECIALIDADE,
  FISCAL_CONFIG,
  HORARIO_AGENDA,
  HOSPITAL_PADRAO,
  HOSPITAL_TELEFONE,
  OWNER_EMAILS,
  PIX_CONFIG,
  SHAREHOLDERS,
};

/* ─────────────────────────────────────────────────────────────
   TIPOS da configuração (mecanismo — o cliente preenche, não define)
   ──────────────────────────────────────────────────────────── */

/** Convênio exibido na vitrine da landing. */
export type Convenio = { name: string; desc: string };

/** Profissional que atende/opera (selects, filtros e badges da agenda). */
export type ClinicDoctor = {
  canonicalName: string;
  shortName: string;
  badgeLetter: string;
  badgeClasses: string;
  matchKeywords: string[];
};

/** Sócio que divide as despesas da clínica (rateio do Financeiro). */
export type Shareholder = {
  id: string;
  name: string;
  shortName: string;
  /** Se true, entra nas despesas divididas em 2 (além das em 3). */
  inTwoWaySplit: boolean;
  /**
   * Se este sócio É um profissional do app, o `canonicalName` dele em
   * `CLINIC_DOCTORS` (liga o rateio ao cadastro central). É isso que faz o card
   * "Resultado do mês" saber qual fatia é do profissional logado.
   * `null` = sócio que não atende no app (ex.: cônjuge que só divide despesa).
   */
  doctorCanonicalName: string | null;
};

/* ─────────────────────────────────────────────────────────────
   VOCABULÁRIO (CRM × CRO) — palavra, nunca comportamento
   ──────────────────────────────────────────────────────────── */

const VOCAB = {
  medico: {
    /** Conselho profissional — rótulo antes do número (documentos, landing). */
    councilLabel: "CRM",
    /** Como o cliente chama o que ele faz (singular/plural). */
    procedureNoun: "procedimento",
    procedureNounPlural: "procedimentos",
    /** Como se chama quem atende. Minúsculo: entra no meio da frase. */
    professionalNoun: "médico",
    professionalNounPlural: "médicos",
    /** Mesma palavra capitalizada, pra rótulo e início de frase. */
    professionalLabel: "Médico",
    /**
     * Adjetivo dos documentos e do ato ("atestado ___", "cuidados ___",
     * "consulta ___"). É o que separa "atestado médico" de "atestado
     * odontológico" — não trocar por `professionalNoun`, que é a pessoa.
     */
    careAdjective: "médico",
    careAdjectiveFem: "médica",
    careAdjectivePlural: "médicos",
  },
  odonto: {
    councilLabel: "CRO",
    procedureNoun: "tratamento",
    procedureNounPlural: "tratamentos",
    professionalNoun: "dentista",
    professionalNounPlural: "dentistas",
    professionalLabel: "Dentista",
    careAdjective: "odontológico",
    careAdjectiveFem: "odontológica",
    careAdjectivePlural: "odontológicos",
  },
} as const;

/** Vocabulário resolvido do cliente. Use em rótulo de tela e documento. */
export const CLINIC_VOCAB = VOCAB[CLINIC_TYPE];

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ──────────────────────────────────────────────────────────── */

/** Classes do badge pra um profissional não reconhecido (texto livre fora do cadastro). */
export const UNKNOWN_DOCTOR_BADGE = "bg-slate-100 text-slate-600 ring-slate-200";

/**
 * Casa um texto livre de profissional (ex.: "fulano de tal", "Dr. Fulano") com
 * o cadastro `CLINIC_DOCTORS`, ou `null` se não reconhecer. Primeira keyword
 * que bater ganha.
 */
export function matchDoctor(raw: string | null | undefined): ClinicDoctor | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  return CLINIC_DOCTORS.find((d) => d.matchKeywords.some((k) => lower.includes(k.toLowerCase()))) ?? null;
}

export function isOwnerEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return OWNER_EMAILS.includes(email.toLowerCase());
}

/** Endereço completo formatado (uma linha) — usado em vários documentos. */
export function fullAddress(): string {
  return `${CLINIC_INFO.addressLine} - ${CLINIC_INFO.neighborhood}`;
}

/** Cidade + UF formatada. */
export function cityState(): string {
  return `${CLINIC_INFO.city} - ${CLINIC_INFO.state}`;
}

/** Pix só funciona com uma chave preenchida — igual ao NFS-e exigir token. */
export function isPixConfigured(): boolean {
  return PIX_CONFIG.key.trim().length > 0;
}

/** Como o assistente de WhatsApp agenda: "direto" cria a consulta; "solicitacao" cria um pedido para a recepção. */
export function bookingMode(): "direto" | "solicitacao" {
  return ASSISTENTE_CONFIG.bookingMode;
}
